/**
 * Fall-stage effect pictures, baked once through the art registry (render-only; nothing here
 * feeds physics):
 *   img/rockChunk       faceless boulder chunk for debris (128×128, the boulder's stone palette);
 *                       debris used to show the whole boulder squashed square, grumpy face and all
 *   fall/warnMarker     edge-hazard marker: inked red/yellow warning sign with a "!" over a down
 *                       chevron (a boulder is coming down this edge)
 *   fall/shieldBubble   the cartoon bubble shield: translucent cel fill, honeycomb near the rim,
 *                       shade crescent lower right, inked gold rim, lit arc upper left
 *   fall/shieldHex      a small inked gold hex that twinkles on the bubble
 *   fall/telegraphRing  inked dashed gold ring (shield pickup spawn telegraph)
 *   fall/speedLine      a tapered white speed streak for fast movers
 *   fall/flaskFlash     white silhouette of the 256×383 flask frame (hit flash over the skin)
 *
 * Every drawer paints in logical frame units; the registry bakes at 2× once and the renderers
 * only transform the sprite each frame.
 */
import { defineToonArt, getToonArtCanvas } from "../art-registry.js";
import { INK } from "../palette.js";
import { TAU, glint, inkStyle } from "../toon-kit.js";
import { mapPts, roundedPolyPath, polyPath } from "../props/prop-kit.js";
import { flaskPath } from "../props/flask-art.js";

/** Art keys (frames are the logical sizes below). */
export var FALL_FX_ART = {
  rockChunk: "img/rockChunk",
  warnMarker: "fall/warnMarker",
  shieldBubble: "fall/shieldBubble",
  shieldHex: "fall/shieldHex",
  telegraphRing: "fall/telegraphRing",
  speedLine: "fall/speedLine",
  flaskFlash: "fall/flaskFlash",
};

/** Bubble frame: 256² with the bubble radius 118 (ink stays inside the frame). */
export var SHIELD_BUBBLE_FRAME = { size: 256, radius: 118 };
/** Telegraph ring frame: 160² with the dash ring centred on radius 64. */
export var TELEGRAPH_RING_FRAME = { size: 160, radius: 64 };
/** Warning marker frame (w × h); the sign's centre sits at `signY`, the arrow tip at `tipY`. */
export var WARN_MARKER_FRAME = { w: 96, h: 124, signY: 48, tipY: 118 };
/** Speed streak frame: head on the right (x = w − headR), tail point on the left. */
export var SPEED_LINE_FRAME = { w: 128, h: 20 };

// The boulder's stone (render/toon/props/fall-props.js STONE), so chunks read as its pieces.
var STONE = {
  light: "#d9cdbf",
  base: "#ad9f93",
  shade: "#8a7b70",
  deep: "#6a5c53",
  ridge: "#f1e7d8",
  speck: "#8f8176",
  fresh: "#e6dccd",
};

/** Chunk outline in unit space: an angular broken piece, flatter than the whole boulder. */
var CHUNK = [
  [-0.94, 0.2], [-0.74, -0.56], [-0.16, -0.92], [0.5, -0.82], [0.94, -0.2],
  [0.8, 0.56], [0.24, 0.9], [-0.54, 0.82],
];
/** Lit top plane and shaded right plane (unit space, clipped to the chunk). */
var CHUNK_TOP = [[-1.4, 0.02], [-0.52, -0.2], [0.08, -0.3], [0.6, -0.52], [1.4, -0.62], [1.4, -1.4], [-1.4, -1.4]];
var CHUNK_RIGHT = [[0.6, -0.52], [0.46, 0.06], [0.18, 1.4], [1.4, 1.4], [1.4, -0.62]];
/** The fresh break: a pale facet on the lower left where the chunk split off. */
var CHUNK_FRESH = [[-0.94, 0.2], [-0.5, 0.08], [-0.28, 0.5], [-0.54, 0.82]];

function linePath(c, pts) {
  for (var i = 0; i < pts.length; i++) {
    if (i) c.lineTo(pts[i][0], pts[i][1]); else c.moveTo(pts[i][0], pts[i][1]);
  }
}

/**
 * Faceless rock chunk (debris). Shown about 20–36 px across, so the ink is heavier than the
 * catalog ratio and the planes are few and bold.
 */
export function drawRockChunk(c, w, h) {
  var cx = w * 0.5, cy = h * 0.52;
  var sx = w * 0.42, sy = h * 0.4;
  var line = Math.min(w, h) * 0.07;
  var pts = mapPts(CHUNK, sx, sy, cx, cy);
  var corner = Math.min(w, h) * 0.06;
  function rock(cc) { roundedPolyPath(cc, pts, corner); }

  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  c.beginPath();
  rock(c);
  c.fillStyle = STONE.base;
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  polyPath(c, CHUNK_TOP, sx, sy, cx, cy);
  c.fillStyle = STONE.light;
  c.fill();
  c.beginPath();
  polyPath(c, CHUNK_RIGHT, sx, sy, cx, cy);
  c.fillStyle = STONE.shade;
  c.fill();
  c.beginPath();
  polyPath(c, CHUNK_FRESH, sx, sy, cx, cy);
  c.fillStyle = STONE.fresh;
  c.fill();
  // Ridge where the top plane turns to the front.
  c.beginPath();
  linePath(c, mapPts(CHUNK_TOP.slice(0, 4), sx, sy, cx, cy));
  c.strokeStyle = STONE.ridge;
  c.lineWidth = line * 0.5;
  c.stroke();
  // Shade crescent along the lower-right rim (the chunk minus itself shifted up-left).
  c.beginPath();
  c.rect(0, 0, w, h);
  c.save();
  c.translate(-sx * 0.12, -sy * 0.15);
  rock(c);
  c.restore();
  c.fillStyle = STONE.deep;
  c.fill("evenodd");
  // A fleck of the boulder's moss on the top corner, so the chunk reads as a piece of it.
  var moss = [[-0.62, -0.62, 0.3], [-0.3, -0.84, 0.24], [-0.86, -0.3, 0.2]];
  for (var m = 0; m < moss.length; m++) {
    c.beginPath();
    c.arc(cx + moss[m][0] * sx, cy + moss[m][1] * sy + sy * 0.05, moss[m][2] * sx, 0, TAU);
    c.fillStyle = "#4c963a";
    c.fill();
  }
  for (var m2 = 0; m2 < moss.length; m2++) {
    c.beginPath();
    c.arc(cx + moss[m2][0] * sx - sx * 0.03, cy + moss[m2][1] * sy - sy * 0.02, moss[m2][2] * sx * 0.9, 0, TAU);
    c.fillStyle = "#79c957";
    c.fill();
  }
  c.beginPath();
  c.ellipse(cx - sx * 0.62, cy - sy * 0.74, sx * 0.1, sx * 0.06, -0.5, 0, TAU);
  c.fillStyle = "#b9ec85";
  c.fill();
  // Two pits with a lit lower lip.
  var pits = [[0.32, 0.24, 0.1], [0.12, -0.42, 0.07]];
  for (var p = 0; p < pits.length; p++) {
    var px = cx + pits[p][0] * sx, py = cy + pits[p][1] * sy, pr = pits[p][2] * sx;
    c.beginPath();
    c.ellipse(px, py, pr, pr * 0.8, 0, 0, TAU);
    c.fillStyle = STONE.speck;
    c.fill();
    c.beginPath();
    c.ellipse(px + pr * 0.3, py + pr * 0.45, pr * 0.7, pr * 0.32, 0, 0, Math.PI);
    c.strokeStyle = "rgba(241,231,216,0.75)";
    c.lineWidth = pr * 0.4;
    c.stroke();
  }
  c.restore();
  // One bold ink round the piece (no inner seams: at 20–36 px they read as scratches).
  c.beginPath();
  rock(c);
  inkStyle(c, line, INK);
  c.stroke();
  glint(c, cx - sx * 0.38, cy - sy * 0.52, Math.min(w, h) * 0.06);
  c.restore();
}

/** A rounded triangle sign (apex up) with corners rounded by `r`. */
function signPath(c, cx, top, half, bottom, r) {
  roundedPolyPath(c, [[cx, top], [cx + half, bottom], [cx - half, bottom]], r);
}

/** Inked warning marker: red/yellow sign with "!" and a chunky down chevron under it. */
export function drawWarnMarker(c, w, h) {
  var cx = w / 2;
  var top = 6, bottom = 80, half = 42;
  var ink = 7;
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  // Down chevron (drawn first so the sign overlaps its top).
  var chev = [[cx - 26, 84], [cx, 104], [cx + 26, 84], [cx + 26, 97], [cx, 118], [cx - 26, 97]];
  c.beginPath();
  roundedPolyPath(c, chev, 4);
  c.fillStyle = "#d2363f";
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  roundedPolyPath(c, mapPts(chev, 1, 1, -2.5, -3), 4);
  c.fillStyle = "#ff5a5f";
  c.fill();
  c.beginPath();
  c.moveTo(cx - 26, 84);
  c.lineTo(cx, 104);
  c.lineTo(cx + 26, 84);
  c.strokeStyle = "#ff9b9b";
  c.lineWidth = 3.5;
  c.stroke();
  c.restore();
  c.beginPath();
  roundedPolyPath(c, chev, 4);
  inkStyle(c, ink * 0.85, INK);
  c.stroke();
  // Sign: red rim (cel shaded), yellow face, ink outline.
  c.beginPath();
  signPath(c, cx, top, half, bottom, 13);
  c.fillStyle = "#d2363f";
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  signPath(c, cx - 2.5, top - 3, half, bottom - 3, 13);
  c.fillStyle = "#ff5a5f";
  c.fill();
  c.restore();
  c.beginPath();
  signPath(c, cx, top + 15, half - 15, bottom - 9, 7);
  c.fillStyle = "#e8a21c";
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  signPath(c, cx - 1.5, top + 13, half - 15, bottom - 11, 7);
  c.fillStyle = "#ffd45c";
  c.fill();
  c.beginPath();
  c.moveTo(cx - 40, 46);
  c.lineTo(cx, 18);
  c.lineTo(cx + 8, 18);
  c.lineTo(cx - 32, 52);
  c.closePath();
  c.fillStyle = "#fff3b8";
  c.fill();
  c.restore();
  c.beginPath();
  signPath(c, cx, top + 15, half - 15, bottom - 9, 7);
  inkStyle(c, 2.5, "#a8321a");
  c.stroke();
  c.beginPath();
  signPath(c, cx, top, half, bottom, 13);
  inkStyle(c, ink, INK);
  c.stroke();
  // "!": a tapered bar and a dot, in ink.
  c.beginPath();
  c.moveTo(cx - 5.5, 33);
  c.quadraticCurveTo(cx, 29, cx + 5.5, 33);
  c.lineTo(cx + 3, 58);
  c.quadraticCurveTo(cx, 61, cx - 3, 58);
  c.closePath();
  c.fillStyle = INK;
  c.fill();
  c.beginPath();
  c.arc(cx, 68, 4.6, 0, TAU);
  c.fill();
  glint(c, cx - 15, 40, 3.2);
  c.restore();
}

/** Pointy-top hexagon path. */
function hexPath(c, x, y, r) {
  for (var i = 0; i < 6; i++) {
    var a = -Math.PI / 2 + i * (Math.PI / 3);
    var px = x + r * Math.cos(a), py = y + r * Math.sin(a);
    if (i) c.lineTo(px, py); else c.moveTo(px, py);
  }
  c.closePath();
}

/** The bubble shield, centred in a 256² frame at radius 118. */
export function drawShieldBubble(c, w, h) {
  var cx = w / 2, cy = h / 2;
  var R = SHIELD_BUBBLE_FRAME.radius * (Math.min(w, h) / SHIELD_BUBBLE_FRAME.size);
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  // Translucent warm body.
  c.beginPath();
  c.arc(cx, cy, R, 0, TAU);
  c.fillStyle = "rgba(255, 220, 120, 0.15)";
  c.fill();
  c.save();
  c.clip();
  // Honeycomb, stepped stronger toward the rim (hard steps, no gradient).
  var hs = R * 0.15;
  var hw = hs * Math.sqrt(3), hh = hs * 1.5;
  var row = 0;
  for (var y = cy - R - hh; y <= cy + R + hh; y += hh, row++) {
    var off = (row % 2) * hw * 0.5;
    for (var x = cx - R - hw; x <= cx + R + hw; x += hw) {
      var hx = x + off;
      var d = Math.hypot(hx - cx, y - cy) / R;
      if (d < 0.5) continue;
      c.beginPath();
      hexPath(c, hx, y, hs * 0.86);
      c.strokeStyle = d > 0.74 ? "rgba(255, 240, 190, 0.5)" : "rgba(255, 240, 190, 0.24)";
      c.lineWidth = R * 0.016;
      c.stroke();
    }
  }
  // Shade crescent lower right: the disc minus itself shifted up-left.
  c.beginPath();
  c.rect(0, 0, w, h);
  c.arc(cx - R * 0.11, cy - R * 0.14, R * 0.97, 0, TAU);
  c.fillStyle = "rgba(255, 150, 40, 0.3)";
  c.fill("evenodd");
  // Inner glow band just inside the rim (a flat band, not a blur).
  c.beginPath();
  c.arc(cx, cy, R - R * 0.085, 0, TAU);
  c.strokeStyle = "rgba(255, 214, 102, 0.42)";
  c.lineWidth = R * 0.07;
  c.stroke();
  c.restore();
  // Gold rim band with a darker inner edge, a lit stretch upper left, then the ink.
  c.beginPath();
  c.arc(cx, cy, R - R * 0.045, 0, TAU);
  c.strokeStyle = "#ffc84a";
  c.lineWidth = R * 0.06;
  c.stroke();
  c.beginPath();
  c.arc(cx, cy, R - R * 0.08, 0, TAU);
  c.strokeStyle = "rgba(224, 138, 28, 0.85)";
  c.lineWidth = R * 0.014;
  c.stroke();
  c.beginPath();
  c.arc(cx, cy, R - R * 0.045, Math.PI * 1.02, Math.PI * 1.48);
  c.strokeStyle = "#fff3b8";
  c.lineWidth = R * 0.026;
  c.stroke();
  c.beginPath();
  c.arc(cx, cy, R, 0, TAU);
  inkStyle(c, R * 0.042, INK);
  c.stroke();
  // Bubble sheen: a bold white arc upper left with a dot after it, and a thin one lower right.
  c.beginPath();
  c.arc(cx, cy, R * 0.76, Math.PI * 1.1, Math.PI * 1.36);
  c.strokeStyle = "rgba(255, 255, 255, 0.92)";
  c.lineWidth = R * 0.08;
  c.stroke();
  c.beginPath();
  var da = Math.PI * 1.45;
  c.arc(cx + Math.cos(da) * R * 0.76, cy + Math.sin(da) * R * 0.76, R * 0.042, 0, TAU);
  c.fillStyle = "rgba(255, 255, 255, 0.92)";
  c.fill();
  c.beginPath();
  c.arc(cx, cy, R * 0.82, Math.PI * 0.08, Math.PI * 0.3);
  c.strokeStyle = "rgba(255, 255, 255, 0.55)";
  c.lineWidth = R * 0.035;
  c.stroke();
  c.restore();
}

/** A small inked gold hexagon with a lit face and a glint (48² frame). */
export function drawShieldHex(c, w, h) {
  var cx = w / 2, cy = h / 2, r = Math.min(w, h) * 0.36;
  c.save();
  c.lineJoin = "round";
  c.beginPath();
  hexPath(c, cx, cy, r);
  c.fillStyle = "#e08a1c";
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  hexPath(c, cx - r * 0.12, cy - r * 0.16, r * 0.96);
  c.fillStyle = "#ffc84a";
  c.fill();
  c.beginPath();
  hexPath(c, cx - r * 0.3, cy - r * 0.36, r * 0.55);
  c.fillStyle = "#fff3b8";
  c.fill();
  c.restore();
  c.beginPath();
  hexPath(c, cx, cy, r);
  inkStyle(c, r * 0.2, INK);
  c.stroke();
  glint(c, cx - r * 0.3, cy - r * 0.36, r * 0.18);
  c.restore();
}

/** Inked dashed gold ring (160² frame, ring radius 64). */
export function drawTelegraphRing(c, w, h) {
  var cx = w / 2, cy = h / 2;
  var R = TELEGRAPH_RING_FRAME.radius * (Math.min(w, h) / TELEGRAPH_RING_FRAME.size);
  var n = 12;
  var span = (TAU / n) * 0.56;
  c.save();
  c.lineCap = "round";
  for (var pass = 0; pass < 3; pass++) {
    for (var i = 0; i < n; i++) {
      var a0 = (i / n) * TAU;
      c.beginPath();
      c.arc(cx, cy, R, a0, a0 + span);
      if (pass === 0) {
        c.strokeStyle = INK;
        c.lineWidth = R * 0.19;
      } else if (pass === 1) {
        c.strokeStyle = "#ffc84a";
        c.lineWidth = R * 0.1;
      } else {
        // Lit edge on the outer side of each dash.
        c.beginPath();
        c.arc(cx, cy, R + R * 0.022, a0 + span * 0.15, a0 + span * 0.7);
        c.strokeStyle = "#fff3b8";
        c.lineWidth = R * 0.035;
      }
      c.stroke();
    }
  }
  c.restore();
}

/** A tapered white streak: round head on the right, point on the left (128 × 20). */
export function drawSpeedLine(c, w, h) {
  var cy = h / 2, headR = h * 0.36, hx = w - headR - 2;
  c.save();
  c.beginPath();
  c.moveTo(2, cy);
  c.quadraticCurveTo(w * 0.5, cy - headR * 0.9, hx, cy - headR);
  c.arc(hx, cy, headR, -Math.PI / 2, Math.PI / 2);
  c.quadraticCurveTo(w * 0.5, cy + headR * 0.9, 2, cy);
  c.closePath();
  c.fillStyle = "#ffffff";
  c.fill();
  // Vapour edge: a darker tone of its own (pale sky) instead of ink.
  c.lineJoin = "round";
  c.lineWidth = h * 0.09;
  c.strokeStyle = "#a9daf2";
  c.stroke();
  c.restore();
}

/** White flask silhouette in the flask frame (256 × 383), grown a hair past the ink. */
export function drawFlaskFlash(c) {
  c.save();
  c.beginPath();
  flaskPath(c, 5);
  c.fillStyle = "#ffffff";
  c.fill();
  c.restore();
}

defineToonArt(FALL_FX_ART.rockChunk, { w: 128, h: 128, name: "rockChunk", draw: drawRockChunk });
defineToonArt(FALL_FX_ART.warnMarker, { w: WARN_MARKER_FRAME.w, h: WARN_MARKER_FRAME.h, name: "warnMarker", draw: drawWarnMarker });
defineToonArt(FALL_FX_ART.shieldBubble, { w: 256, h: 256, name: "shieldBubble", draw: drawShieldBubble });
defineToonArt(FALL_FX_ART.shieldHex, { w: 48, h: 48, name: "shieldHex", draw: drawShieldHex });
defineToonArt(FALL_FX_ART.telegraphRing, { w: 160, h: 160, name: "telegraphRing", draw: drawTelegraphRing });
defineToonArt(FALL_FX_ART.speedLine, { w: SPEED_LINE_FRAME.w, h: SPEED_LINE_FRAME.h, name: "speedLine", draw: drawSpeedLine });
defineToonArt(FALL_FX_ART.flaskFlash, { w: 256, h: 383, name: "flaskFlash", draw: drawFlaskFlash });

/**
 * The baked canvas for one of FALL_FX_ART's names (null without a 2D canvas, e.g. jsdom).
 * @param {keyof typeof FALL_FX_ART} name
 */
export function getFallFxArt(name) {
  var key = FALL_FX_ART[name];
  return key ? getToonArtCanvas(key) : null;
}

/** Debris picture (FallPhysics `_getDebrisImg`): the faceless rock chunk. */
export function getRockChunkImage() {
  return getToonArtCanvas(FALL_FX_ART.rockChunk);
}
